/**
 * Bank Transactions View — 2-tab layout: Transactions by Party + Agent feed.
 *
 * Reads from the `bankTransactionEntries` table, groups client-side by counterpartyName.
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  Landmark,
  Bot,
  XCircle,
  Loader2,
  MessageSquare,
  Wand2,
  TableProperties,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { UnifiedFeed } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { BankTransactionsExportButton } from "./BankTransactionsExportButton";
import { BankTransactionsDocumentsPanel } from "./BankTransactionsDocumentsPanel";
import { CashMovementsPanel } from "./CashMovementsPanel";
import { AllTransactionsPanel } from "./AllTransactionsPanel";
import { useAllBankTransactions } from "./useAllBankTransactions";
import type { ExportTxn, ExportAccount } from "./buildBankTransactionsExport";

interface BankTransactionsViewProps {
  taskId: Id<"summarizationTasks">;
  jobId: Id<"jobs">;
  summarizationRunId: Id<"summarizationRuns">;
  onBack: () => void;
  onRerun: () => void;
  isViewer?: boolean;
}

export function BankTransactionsView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
}: BankTransactionsViewProps) {
  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  const { transactions: rawTransactions, isLoadingAll } =
    useAllBankTransactions(jobId);

  const { data: rawCanonicalAccounts } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getCanonicalBankAccounts, {
      taskId,
    }),
  );

  const { mutateAsync: cancelTask, isPending: isCancelling } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .cancelBankTransactionsTask,
    ),
  });

  const handleCancel = async () => {
    try {
      await cancelTask({ taskId });
      toast.success("Bank transactions agent cancelled");
    } catch {
      toast.error("Failed to cancel");
    }
  };

  const [chatOpen, setChatOpen] = useState(false);
  const [narrativeChatPrompt, setNarrativeChatPrompt] = useState<
    string | undefined
  >(undefined);

  const txns = useMemo(
    () => (rawTransactions ?? []) as ExportTxn[],
    [rawTransactions],
  );
  const accounts = useMemo(
    () => (rawCanonicalAccounts ?? []) as ExportAccount[],
    [rawCanonicalAccounts],
  );

  // Documents that contributed transactions — value is the stable document id.
  const documentOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of txns) {
      if (!t.sourceDocumentId) continue;
      const label =
        t.sourceDocumentName ?? t.sourceFileName ?? "Unknown document";
      if (!map.has(t.sourceDocumentId)) map.set(t.sourceDocumentId, label);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label, title: label }));
  }, [txns]);

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting";

  const output = task.output as
    | {
        totalTransactions?: number;
        uniqueParties?: number;
        uncategorizedCount?: number;
      }
    | undefined;

  // Uncontrolled tabs (like CorporateStructureView): only sets the initial
  // tab on mount. If we used a controlled `value` derived from `isRunning`,
  // the user would be silently yanked off the live Agent feed onto the
  // Documents tab the moment the run completed or failed.
  return (
    <Tabs
      defaultValue={isRunning ? "feed" : "allTransactions"}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tabs */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">Bank Transactions</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isRunning && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isCancelling || isViewer}
                className="gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                {isCancelling ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <XCircle className="h-3 w-3" />
                )}
                Cancel
              </Button>
            )}
            {isCompleted && task.output && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {isCompleted && task.output && (
              <BankTransactionsExportButton
                txns={txns}
                accounts={accounts}
                documentOptions={documentOptions}
                isLoadingAll={isLoadingAll}
              />
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            <TabsTrigger value="allTransactions" className="gap-1.5">
              <TableProperties className="h-3.5 w-3.5" />
              All Transactions
            </TabsTrigger>
            <TabsTrigger value="transactions" className="gap-1.5">
              <Landmark className="h-3.5 w-3.5" />
              Documents
              {output?.totalTransactions != null &&
                output.totalTransactions > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-0.5 px-1 text-[10px]"
                  >
                    {output.totalTransactions}
                  </Badge>
                )}
            </TabsTrigger>
            <TabsTrigger value="cashMovements" className="gap-1.5">
              <Wand2 className="h-3.5 w-3.5" />
              Cash Movements
            </TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting state */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <div className="rounded-full bg-muted/50 p-4">
            <Landmark className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium">Ready to analyse</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Categorises bank transactions by counterparty type and groups by
              party for investigation analysis.
            </p>
          </div>
        </div>
      )}

      {/* Active / completed — tab content */}
      {!isWaiting && (
        <>
          <TabsContent value="transactions" className="mt-2">
            <BankTransactionsDocumentsPanel
              jobId={jobId}
              taskId={taskId}
              isRunning={isRunning}
              isViewer={isViewer}
            />
          </TabsContent>

          <TabsContent value="allTransactions" className="mt-2">
            <AllTransactionsPanel
              jobId={jobId}
              taskId={taskId}
              isViewer={isViewer}
            />
          </TabsContent>

          <TabsContent value="cashMovements" className="mt-2">
            <CashMovementsPanel
              jobId={jobId}
              isViewer={isViewer}
              initialChatPrompt={narrativeChatPrompt}
              onConsumeInitialChatPrompt={() =>
                setNarrativeChatPrompt(undefined)
              }
            />
          </TabsContent>

          <TabsContent value="feed" className="mt-2">
            <UnifiedFeed
              task={task}
              subAgents={[]}
              taskTree={task.workingMemory?.taskTree}
              isRunning={isRunning}
            />
          </TabsContent>
        </>
      )}

      {isCompleted && task.output && (
        <SummaryChatSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          taskId={taskId}
          jobId={jobId}
          agentName="Bank Transactions"
        />
      )}
      <AiDisclaimer className="mt-4 text-center" />
    </Tabs>
  );
}
